/**
 * audioVersionSlice - Global state for audio version preferences
 *
 * Manages user's selected audio version (bilingual, spanish, vocals, instrumental)
 * and tracks which versions are available for the current song.
 *
 * State persists across routes so user preferences are maintained when navigating
 * between audio-review, practice mode, and study sessions.
 */

import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export type AudioVersion = 'bilingual' | 'spanish' | 'vocals' | 'instrumental'

export interface AudioVersionState {
  /** Currently selected audio version for playback */
  selectedVersion: AudioVersion

  /** Tracks which audio versions are available for current song */
  availableVersions: {
    bilingual: boolean
    spanish: boolean
    vocals: boolean
    instrumental: boolean
  }
}

const initialState: AudioVersionState = {
  selectedVersion: 'bilingual', // Default to bilingual version
  availableVersions: {
    bilingual: false,
    spanish: false,
    vocals: false,
    instrumental: false,
  },
}

const audioVersionSlice = createSlice({
  name: 'audioVersion',
  initialState,
  reducers: {
    /**
     * Set the selected audio version
     * Updates global state so preference persists across routes
     */
    setSelectedVersion: (state, action: PayloadAction<AudioVersion>) => {
      state.selectedVersion = action.payload
    },

    /**
     * Update which audio versions are available for current song
     * Used when loading a new song to enable/disable tabs
     */
    setAvailableVersions: (
      state,
      action: PayloadAction<Partial<AudioVersionState['availableVersions']>>
    ) => {
      state.availableVersions = {
        ...state.availableVersions,
        ...action.payload,
      }
    },

    /**
     * Reset to default state
     * Call when navigating away from audio contexts
     */
    resetAudioVersion: (state) => {
      state.selectedVersion = initialState.selectedVersion
      state.availableVersions = initialState.availableVersions
    },
  },
})

export const { setSelectedVersion, setAvailableVersions, resetAudioVersion } =
  audioVersionSlice.actions

export default audioVersionSlice.reducer
